import { systemLutsStore } from '@/services/indexedDB.service';
// import LUTCubeLoader from '@/assets/js/loaders/LUTCubeLoader';
import { LUTCubeLoader } from 'postprocessing';
import { LinearFilter } from 'three';

export default class Luts {
    constructor() {
        this.lutsLoadedInterval;
        this.IDT = config.IDT; // loading from config
        this.IDTluts = []; // fetched IDT luts
        this.ODT = config.ODT; // loading from config
        this.LUTcontrols = []; // LUT controls
        this.customLUT = {}; // User's LUTs
        this.LUTsliders = []; // LUT sliders (pseudo controls)
        this.loaded = {
            // identity: { required: true, status: false },
            // creative: {required: true, status: false},
            LUTcontrols: { required: false, controls: [] },
            LUTsliders: { required: false, controls: [] },
            customLUT: { required: false, initialLoad: true, folders: [] },
        };
        this.IPP2 = [];
        this.davinci = [];
        this.logc4 = [];
        this.customLutsBlockColourSpaceLuts = [];
    }

    async prepareExportLuts(data) {
        let metaData = {
            meta: {
                createdBy: '# Created by: Lutify.me LUTs Previewer Export ',
                website: '# Website: https://preview.lutify.me ',
                title: `TITLE "${data.name}"`,
            },
            options: data.options,
        };

        let exportLuts = [];

        if (!luts.identity33) await this.fetchIdentityLUT(33);
        let exportData = await gpu.getExportData(33, 'ACES', metaData);
        let contentMeta = this.getExportContentMeta(metaData, exportData.blocksData, 33);
        let contentPixels = this.getExportContentPixels(exportData.pixels);

        exportLuts.push(new File([contentMeta + contentPixels], `${data.name}.cube`, { type: 'text/plain' }));

        return exportLuts;
    }

    async prepareDownloadLuts(data) {
        let metaData = {
            meta: {
                createdBy: '# Created by: Lutify.me LUTs Previewer Export ',
                website: '# Website: https://preview.lutify.me ',
                title: `TITLE "${data.name}"`,
            },
            options: data.options,
        };

        let exportLuts = [];
        let payload = {};

        if (!luts.identity33) await this.fetchIdentityLUT(33);
        let exportData = await gpu.getExportData(33, 'ACES', metaData);
        payload.cube_pixels = exportData.pixels.toString();
        payload.cube_meta = this.getExportContentMeta(metaData, exportData.blocksData, 33);

        let response = await vm.$store.dispatch('Files/downloadLuts', payload);
        if (!response) {
            vm.$snackbar('Download of luts failed.', 'error');
            return;
        }

        exportLuts.push(new File([response.data.cube], `${data.name}.cube`, { type: 'text/plain' }));

        return exportLuts;
    }

    getExportContentMeta(data, blocksData, lutSize, skip = true) {
        let blocksMeta = '# META\n';

        blocksData.forEach((block, i) => {
            if (block.type == 'fixed' && skip) return;
            blocksMeta += skip ? `# Block ${i}: ` : `# Block ${i + 1}: `;

            if (block.type == 'fixed') {
                return;
            } else if (block.control == 'LUTcontrols') {
                blocksMeta += `LUT | ${block.name}: ${block.selectedPreviewName} (${block.opacity}%)`;
                blocksMeta += '\n';
            } else if (block.control == 'customLUT') {
                blocksMeta += `Custom LUT | Folder: ${JSON.stringify(block.folder)} | LUT: ${JSON.stringify(block.lut)} | ColorSpace: ${JSON.stringify(block.colorSpace)} | Opacity: ${block.opacity}%`;
                blocksMeta += '\n';
            } else {
                blocksMeta += `${block.name} | Blending Mode: ${block.blendingModeName} | `;
                blocksMeta += JSON.stringify(block.values);
                blocksMeta += '\n';
            }
        });

        // Appending some meta on top in file
        let text = `${data.meta.createdBy} \n`;
        text += `${data.meta.website} \n\n`;
        text += blocksMeta + '\n';

        if (data.meta.description) text += `${data.meta.description}\n\n`;

        // text += `SOURCE ${JSON.stringify(blocksData)}`;
        // text += "\n\n";

        text += `${data.meta.title} \n\n`;
        text += 'LUT_3D_SIZE ' + lutSize + '\n';

        return text;
    }

    getExportContentPixels(pixels, vlt = false) {
        let content = '';

        for (let j = 0; j < pixels.length; j += 4) {
            content +=
                pixels[j].toFixed(6).toString() + ' ' + pixels[j + 1].toFixed(6).toString() + ' ' + pixels[j + 2].toFixed(6).toString() + '\n';
        }

        return content;
    }

    async fetchIdentityLUT(size) {
        let fetchedLUT = await systemLutsStore.getItem(`identity-${size}`);

        if (!fetchedLUT) {
            let fetchedLUTData = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                type: 'identity',
                category: `Identity-${size}x${size}x${size}.cube`,
            });

            fetchedLUT = await vm.$store.dispatch('Projects/fetchFile', fetchedLUTData.presignedUrl);
            fetchedLUT = new File([fetchedLUT], fetchedLUTData.fileName, { type: 'text/plain' });

            await systemLutsStore.setItem(`identity-${size}`, fetchedLUT);
        }

        luts[`identity${size}`] = fetchedLUT;
    }

    async fetchIPP2ODT() {
        let fetchedLUT = await systemLutsStore.getItem('ACEScct-to-RWG'); // checking in indexedDB

        if (!fetchedLUT) {
            let fetchedLUTData = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                type: 'identity',
                category: 'ACEScct-to-RWG.cube',
            });

            fetchedLUT = await vm.$store.dispatch('Projects/fetchFile', fetchedLUTData.presignedUrl);
            fetchedLUT = new File([fetchedLUT], fetchedLUTData.fileName, { type: 'text/plain' });

            systemLutsStore.setItem('ACEScct-to-RWG', fetchedLUT);
        }

        await luts.load([fetchedLUT], luts.IPP2);
    }

    async fetchDavinciIDT() {
        let fetchedLUT = await systemLutsStore.getItem('DWG-to-ACEScct-v2'); // checking in indexedDB

        if (!fetchedLUT) {
            let fetchedLUTData = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                type: 'identity',
                category: 'DWG-to-ACEScct-v2.cube',
            });

            fetchedLUT = await vm.$store.dispatch('Projects/fetchFile', fetchedLUTData.presignedUrl);
            fetchedLUT = new File([fetchedLUT], fetchedLUTData.fileName, { type: 'text/plain' });

            systemLutsStore.setItem('DWG-to-ACEScct-v2', fetchedLUT);
        }

        await luts.load([fetchedLUT], luts.davinci);
    }

    async fetchDavinciODT() {
        let fetchedLUT = await systemLutsStore.getItem('ACEScct-to-DWG-v2'); // checking in indexedDB

        if (!fetchedLUT) {
            let fetchedLUTData = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                type: 'identity',
                category: 'ACEScct-to-DWG-v2.cube',
            });

            fetchedLUT = await vm.$store.dispatch('Projects/fetchFile', fetchedLUTData.presignedUrl);
            fetchedLUT = new File([fetchedLUT], fetchedLUTData.fileName, { type: 'text/plain' });

            systemLutsStore.setItem('ACEScct-to-DWG-v2', fetchedLUT);
        }

        await luts.load([fetchedLUT], luts.davinci);
    }

    async fetchLogC4IDT() {
        const lutName = 'LogC4-to-ACEScct';
        let fetchedLUT = await systemLutsStore.getItem(lutName);

        if (!fetchedLUT) {
            let fetchedLUTData = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                type: 'identity',
                category: `${lutName}.cube`,
            });

            fetchedLUT = await vm.$store.dispatch('Projects/fetchFile', fetchedLUTData.presignedUrl);
            fetchedLUT = new File([fetchedLUT], fetchedLUTData.fileName, { type: 'text/plain' });

            systemLutsStore.setItem(lutName, fetchedLUT);
        }

        await luts.load([fetchedLUT], luts.logc4);
    }

    async fetchLogC4ODT() {
        const lutName = 'ACEScct-to-LogC4';
        let fetchedLUT = await systemLutsStore.getItem(lutName); // checking in indexedDB

        if (!fetchedLUT) {
            let fetchedLUTData = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                type: 'identity',
                category: `${lutName}.cube`,
            });

            fetchedLUT = await vm.$store.dispatch('Projects/fetchFile', fetchedLUTData.presignedUrl);
            fetchedLUT = new File([fetchedLUT], fetchedLUTData.fileName, { type: 'text/plain' });

            systemLutsStore.setItem(lutName, fetchedLUT);
        }

        await luts.load([fetchedLUT], luts.logc4);
    }

    fetchAll() {
        // return Promise.all([this.fetchIDTLUTs()]);
    }

    get(title, category) {
        return this[category].find(LUT => LUT.title == title);
    }

    async fetchLUTcontrols(controlData) {
        let store;
        if (controlData.control == 'LUTcontrols') store = LUTcontrolsStore;
        else store = LUTslidersStore;

        let fetchedLUTs = await store.getItem(controlData.category);

        if (!fetchedLUTs) {
            let fetchedLUTDataList = [];

            if (controlData.product) {
                fetchedLUTDataList = await vm.$store.dispatch('Products/fetchLuts', controlData.product);
            } else {
                fetchedLUTDataList = await vm.$store.dispatch('Projects/fetchSystemLuts', {
                    type: 'controls',
                    category: controlData.control == 'LUTcontrols' ? 'blocks/' + controlData.category : 'sliders/' + controlData.category,
                });
            }

            let promises = [];
            fetchedLUTDataList.forEach(LUT => {
                promises.push(vm.$store.dispatch('Projects/fetchFile', LUT.presignedUrl));
            });

            fetchedLUTs = await Promise.all(promises);

            fetchedLUTs.forEach((blob, key) => {
                fetchedLUTs[key] = new File([blob], fetchedLUTDataList[key].fileName, { type: 'text/plain' });
            });

            store.setItem(controlData.category, fetchedLUTs);
        }

        // console.log(`[FECTHED] ${controlData.control} - ${controlData.category}`);
        return fetchedLUTs;
    }

    areRequiredLUTsLoaded() {
        let status = true;
        for (let LUTcategory in this.loaded) {
            if (this.loaded[LUTcategory].required && !this.loaded[LUTcategory].status) {
                status = false;
            }
        }

        return status;
    }

    getComposerIndexByName(control, category, name) {
        let composerIndex;
        this[control][category].forEach(LUT => {
            if (LUT.title == name && LUT.status !== 'deleted') composerIndex = LUT.composerIndex;
        });
        return composerIndex;
    }

    getComposerIndexById(control, id) {
        let composerIndex;

        Object.keys(this[control]).forEach(folderId => {
            this[control][folderId].forEach(lut => {
                if (lut.lutId == id) composerIndex = lut.composerIndex;
            });
        });

        return composerIndex;
    }

    getFileById(control, id) {
        let file;

        Object.keys(this[control]).forEach(folderId => {
            this[control][folderId].forEach(lut => {
                if (lut.lutId == id) file = lut.file;
            });
        });

        return file;
    }

    async load(LUTList, storeArray = []) {
        let loadedList = [];

        for await (const LUT of LUTList) {
            await new Promise(resolve => {
                let objectURL = URL.createObjectURL(LUT);
                new LUTCubeLoader().load(objectURL, t => {
                    t.minFilter = LinearFilter;
                    t.magFilter = LinearFilter;

                    let result = {
                        texture: t
                    };
                    result.filename = LUT.name;
                    result.title = LUT.name.split('.').filter((value, key) => key < LUT.name.split('.').length - 1).join('.');
                    if (result.title.split('_').length > 1) {
                        result.title = result.title.split('_')[1];
                    }

                    // console.log(result);
                    storeArray.push(result);
                    loadedList.push(result);
                    URL.revokeObjectURL(objectURL);
                    resolve();
                });
            });
        }

        return loadedList;
    }

}
